/**
 * SeatView for Poker Reborn
 *
 * Displays a single seat at the table - either empty or with a player.
 * Shows avatar, name, stack, and hole cards when occupied.
 *
 * @see docs/screens/room/SEAT.md - Full documentation: hierarchy, states, colors, dimensions
 * @see docs/screens/room/TABLE.md - Seat positions and layout
 */

import { Assets, BlurFilter, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { SitButton } from '@/components/table/SitButton';
import { PlayerChipsDisplay } from '@/components/table/PlayerChipsDisplay';
import { PlayerInfoBar } from '@/components/table/PlayerInfoBar';
import { TimerBorder } from '@/components/table/TimerBorder';
import { SitoutBadge } from '@/components/table/SitoutBadge';
import type { PlayerState } from '@/types/game';
import { getScaledValue } from '@/utils/responsive';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';
import { DEFAULT_AVATAR_ID, getAvatarPath } from '@/config/avatars';
import { AvatarService } from '@/services/AvatarService';

// =============================================================================
// BBS BREAKPOINTS FOR SEAT DIMENSIONS
// =============================================================================

/**
 * Breakpoint-Based Scaling configuration for seat dimensions.
 * All values use discrete mode (jump at breakpoints, no interpolation).
 * Breakpoints: 375 (phone), 720 (tablet), 1440 (desktop), 1920 (large desktop)
 */
const SEAT_BREAKPOINTS = {
  // Avatar
  avatarSize: {375: 80, 720: 90, 900: 106, 1440: 125, 1920: 138},

  // Font sizes
  badgeFont: { 375: 10, 720: 9, 1440: 10, 1920: 10 },  // Used by winnerBadge and lastActLabel

  // Spacing/positions
  playerInfoBarY: { 375: 46, 720: 58, 900: 65, 1440: 75, 1920: 85 },
  holeCardsY: { 375: -55, 720: -70, 1440: -80, 1920: -80 },
  badgeY: { 375: -32, 720: -40, 1440: -45, 1920: -45 },
  lastActY: { 375: 39, 720: 49, 900: 56, 1440: 65, 1920: 74 },

};

/**
 * Dedicated breakpoints for the "Sit Here" button (empty seat state).
 * Separated for heavy styling customization.
 */
const SIT_BUTTON_BREAKPOINTS = {
  diameter: {375: 64, 720: 72, 900: 85, 1440: 100, 1920: 110},
  fontSize: {375: 14, 720: 16, 1440: 20, 1920: 24},
  borderWidth: {375: 2, 720: 2, 1440: 2, 1920: 2},
};

// =============================================================================
// STYLES
// =============================================================================

/** Color styles (device-independent) */
const COLORS = {
  /** Text colors */
  text: {
    name: 0xffffff,
    stack: 0xffd700,
  },

  /** Winner badge */
  winnerBadge: {
    fill: 0x4caf50,
    textFill: 0xffffff,
    padding: 6,
  },

  /** Last action badge colors (action color = background, white text) */
  lastAction: {
    textFill: 0xffffff,
    fold: 0x666666,    // Gray
    check: 0x2e7d32,   // Deep green
    call: 0x2e7d32,    // Deep green
    bet: 0xe67e00,     // Deep orange
    raise: 0xe67e00,   // Deep orange
    allin: 0xd32f2f,   // Deep red
    default: 0x555555, // Dark gray fallback
    padding: { x: 8, y: 4 },
    cornerRadius: 4,
  },
};

// =============================================================================
// SEAT VIEW
// =============================================================================

export class SeatView extends Container {
  private seatNumber: number;
  private playerContainer: Container;
  private avatarContainer: Container;
  private playerInfoBar: PlayerInfoBar;
  private sitButton: SitButton | null = null;
  private timerBorder: TimerBorder;
  private sitoutBadge: SitoutBadge;

  // Effect injection points
  private winnerBadge: Container;
  private avatarGlow: Graphics;

  // Last action display
  private lastActContainer: Container;
  private lastActBg: Graphics;
  private lastActLabel: Text;

  // Avatar sprite (loaded from illustrated avatar assets)
  private avatarShadowSprite: Sprite | null = null;
  private avatarSprite: Sprite | null = null;
  private currentAvatarId: number | null = null;
  private avatarLoadGeneration = 0;

  private player: PlayerState | null = null;
  private isActive = false;
  private heroSeated = false;

  /** Current screen width for BBS calculations */
  private currentScreenWidth = 1920;

  /** Callback when empty seat is clicked */
  onSitClick: ((seat: number) => void) | null = null;

  /** Callback when occupied avatar is clicked (for profile popup) */
  onAvatarClick: ((seat: number, player: PlayerState) => void) | null = null;

  /**
   * Get a BBS value for current screen width (discrete mode).
   * Helper method for cleaner code.
   */
  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, { discrete: true });
  }

  constructor(seatNumber: number) {
    super();
    this.seatNumber = seatNumber;

    // Player container groups all occupied-seat elements
    // so sit button can be positioned independently
    this.playerContainer = new Container();
    this.playerContainer.label = 'playerContainer';
    this.addChild(this.playerContainer);

    // Create avatar container
    this.avatarContainer = new Container();
    this.playerContainer.addChild(this.avatarContainer);

    // Create avatar glow (behind avatar, for aura effects)
    this.avatarGlow = new Graphics();
    this.avatarGlow.label = 'avatarGlow';
    this.avatarGlow.visible = false;
    this.avatarContainer.addChild(this.avatarGlow);

    // Create player info bar (name + chips with dark background)
    // Also contains betChipsContainer for chip animations (centered)
    this.playerInfoBar = new PlayerInfoBar({
      screenWidth: this.currentScreenWidth,
    });
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.playerContainer.addChild(this.playerInfoBar);

    // Create timer border (traces playerInfoBar perimeter, rendered on top of it)
    this.timerBorder = new TimerBorder({
      screenWidth: this.currentScreenWidth,
    });
    this.timerBorder.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.timerBorder.visible = false;
    this.playerContainer.addChild(this.timerBorder);

    // Create winner badge (hidden by default)
    this.winnerBadge = new Container();
    this.winnerBadge.label = 'winnerBadge';
    this.winnerBadge.visible = false;
    this.createWinnerBadge();
    this.playerContainer.addChild(this.winnerBadge);

    // Create last action label (hidden by default)
    this.lastActContainer = new Container();
    this.lastActContainer.label = 'lastActLabel';
    this.lastActContainer.visible = false;

    this.lastActBg = new Graphics();
    this.lastActContainer.addChild(this.lastActBg);

    this.lastActLabel = new Text({
      text: '',
      style: {
        fill: 0xffffff,
        fontSize: this.bbs(SEAT_BREAKPOINTS.badgeFont),
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.lastActLabel.anchor.set(0.5);
    this.lastActContainer.addChild(this.lastActLabel);

    // Position over nameLabel in playerInfoBar
    this.lastActContainer.y = this.bbs(SEAT_BREAKPOINTS.lastActY);
    this.playerContainer.addChild(this.lastActContainer);

    // Create sitout badge with countdown (shown when player is sitting out)
    // Added after lastActContainer so it renders on top
    this.sitoutBadge = new SitoutBadge({
      screenWidth: this.currentScreenWidth,
    });
    this.sitoutBadge.y = this.bbs(SEAT_BREAKPOINTS.lastActY);
    this.sitoutBadge.visible = false;
    this.playerContainer.addChild(this.sitoutBadge);

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.seat);

    // Initial render as empty
    this.showEmpty();
  }

  /**
   * Set player for this seat
   */
  setPlayer(player: PlayerState | null): void {
    this.player = player;

    if (player) {
      this.showPlayer(player);
    } else {
      this.showEmpty();
    }
  }

  /**
   * Set whether this seat is active (player's turn)
   */
  setActive(active: boolean): void {
    this.isActive = active;

    if (active) {
      // Sync geometry from PlayerInfoBar and start timer border
      const geo = this.playerInfoBar.getGeometry();
      this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);
      this.timerBorder.setProgress(1.0, true);
      this.timerBorder.show();
    } else {
      this.timerBorder.hide();
    }
  }

  /**
   * Update timer ring progress
   */
  setTimerProgress(progress: number): void {
    if (!this.isActive) return;

    // Sync geometry in case PlayerInfoBar resized (e.g., chip count changed)
    const geo = this.playerInfoBar.getGeometry();
    this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);
    this.timerBorder.setProgress(progress);
  }

  /**
   * Get seat number
   */
  getSeatNumber(): number {
    return this.seatNumber;
  }

  /**
   * Set whether the hero is seated at the table
   * When hero is seated, "Sit Here" buttons should be hidden
   */
  setHeroSeated(seated: boolean): void {
    if (this.heroSeated !== seated) {
      this.heroSeated = seated;
      // Re-render empty seat if currently empty
      if (!this.player) {
        this.showEmpty();
      }
    }
  }

  /**
   * Set the sitout countdown timer
   * @param seconds - Remaining seconds, or null to hide
   */
  setSitoutCountdown(seconds: number | null): void {
    this.sitoutBadge.setCountdown(seconds);
  }


  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private showEmpty(): void {
    // Remove sit button if exists
    if (this.sitButton) {
      this.removeChild(this.sitButton);
      this.sitButton.destroy();
      this.sitButton = null;
    }

    // Clear avatar sprite (free texture reference, reset tracking)
    this.clearAvatarSprite();

    // Hide all player elements
    this.playerContainer.visible = false;

    // Scale button based on screen width using dedicated breakpoints
    const diameter = this.bbs(SIT_BUTTON_BREAKPOINTS.diameter);
    const fontSize = this.bbs(SIT_BUTTON_BREAKPOINTS.fontSize);
    const borderWidth = this.bbs(SIT_BUTTON_BREAKPOINTS.borderWidth);
    const isWatching = !this.heroSeated;

    this.sitButton = new SitButton({
      diameter,
      showLabel: isWatching,
      interactive: isWatching,
      style: {
        borderWidth,
        fontSize,
        ...(!isWatching && { borderColor: 0x2a2a40 }),
      },
    });

    if (isWatching) {
      this.sitButton.onClick = () => {
        if (this.onSitClick) {
          this.onSitClick(this.seatNumber);
        }
      };
    }

    // Center the button (SitButton anchor is top-left, so offset by half)
    this.sitButton.x = -diameter / 2;
    this.sitButton.y = -diameter / 2;
    this.addChild(this.sitButton);

    // Move origin marker to top (if debug enabled)
    const originMarker = this.children.find((c) => c.label === 'originMarker');
    if (originMarker) {
      this.addChild(originMarker); // Re-adding moves to top
    }
  }

  private showPlayer(player: PlayerState): void {
    // Remove sit button if exists
    if (this.sitButton) {
      this.removeChild(this.sitButton);
      this.sitButton.destroy();
      this.sitButton = null;
    }

    // Show player elements
    this.playerContainer.visible = true;
    this.avatarContainer.visible = true;
    this.playerInfoBar.visible = true;

    // Make avatar clickable for profile popup
    this.avatarContainer.removeAllListeners('pointerdown');
    this.avatarContainer.eventMode = 'static';
    this.avatarContainer.cursor = 'pointer';
    this.avatarContainer.on('pointerdown', (event) => {
      event.stopPropagation();
      if (this.onAvatarClick && this.player) {
        this.onAvatarClick(this.seatNumber, this.player);
      }
    });

    // Draw avatar
    this.drawAvatar(player);

    // Update player info bar
    this.playerInfoBar.setName(player.profile.name);
    const shouldAnimate = this.playerInfoBar.getChipsDisplay().getChips() > 0;
    this.playerInfoBar.setChips(player.stack, shouldAnimate);
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);

    // Show sitout badge if player is sitting out
    this.sitoutBadge.visible = player.status === 'sitout';

    // Update last action display
    this.updateLastAction(player.lastAction);
  }

  private drawAvatar(player: PlayerState): void {
    const size = this.bbs(SEAT_BREAKPOINTS.avatarSize);

    // Load avatar sprite (use default avatar as fallback)
    const avatarId = this.getPlayerAvatarId(player) ?? DEFAULT_AVATAR_ID;
    if (avatarId !== this.currentAvatarId) {
      this.currentAvatarId = avatarId;
      void this.loadAvatarSprite(avatarId, size);
    } else if (this.avatarSprite) {
      // Same avatar, just resize the existing sprite
      this.updateAvatarSpriteSize(size);
    }

    // Show folded state (dim the seat)
    if (player.status === 'folded') {
      this.alpha = 0.5;
    } else {
      this.alpha = 1;
    }
  }

  /**
   * Get avatar ID for a player from CRM profile data.
   */
  private getPlayerAvatarId(player: PlayerState): number | null {
    return AvatarService.resolveAvatarId(player.profile.crmData);
  }

  /**
   * Async-load an avatar sprite with circular mask.
   * Uses a generation counter to discard stale loads.
   */
  private async loadAvatarSprite(avatarId: number, size: number): Promise<void> {
    const generation = ++this.avatarLoadGeneration;
    const path = getAvatarPath(avatarId);

    try {
      const texture = await Assets.load<Texture>(path);

      // Check if this load is still relevant
      if (generation !== this.avatarLoadGeneration || this.destroyed) return;

      // Create or update shadow sprite (black tinted + blurred duplicate)
      if (!this.avatarShadowSprite) {
        this.avatarShadowSprite = new Sprite(texture);
        this.avatarShadowSprite.label = 'avatarShadow';
        this.avatarShadowSprite.anchor.set(0.5);
        this.avatarShadowSprite.y = -10;
        this.avatarShadowSprite.tint = 0x000000;
        this.avatarShadowSprite.alpha = 0.55;
        this.avatarShadowSprite.filters = [new BlurFilter({ strength: 4 })];
        this.avatarContainer.addChild(this.avatarShadowSprite);
      } else {
        this.avatarShadowSprite.texture = texture;
      }

      // Create or update sprite
      if (!this.avatarSprite) {
        this.avatarSprite = new Sprite(texture);
        this.avatarSprite.label = 'avatarSprite';
        this.avatarSprite.anchor.set(0.5);
        this.avatarSprite.y = -10;
        this.avatarContainer.addChild(this.avatarSprite);
      } else {
        this.avatarSprite.texture = texture;
      }

      this.updateAvatarSpriteSize(size);
    } catch {
      // Failed to load — clear stale state
      if (generation === this.avatarLoadGeneration) {
        this.clearAvatarSprite();
      }
    }
  }

  /** Remove avatar sprite, reset tracking */
  private clearAvatarSprite(): void {
    this.currentAvatarId = null;

    if (this.avatarShadowSprite) {
      this.avatarContainer.removeChild(this.avatarShadowSprite);
      this.avatarShadowSprite.destroy();
      this.avatarShadowSprite = null;
    }

    if (this.avatarSprite) {
      this.avatarContainer.removeChild(this.avatarSprite);
      this.avatarSprite.destroy();
      this.avatarSprite = null;
    }
  }

  /** Resize existing avatar sprite and its shadow to new size */
  private updateAvatarSpriteSize(size: number): void {
    const shadowOffset = size * 0.06;
    if (this.avatarShadowSprite) {
      this.avatarShadowSprite.width = size;
      this.avatarShadowSprite.height = size;
      this.avatarShadowSprite.y = -10 + shadowOffset;
    }
    if (this.avatarSprite) {
      this.avatarSprite.width = size;
      this.avatarSprite.height = size;
      this.avatarSprite.visible = true;
    }
  }

  /**
   * Update the last action display
   */
  private updateLastAction(action: PlayerState['lastAction'] | undefined): void {
    if (!action) {
      this.lastActContainer.visible = false;
      return;
    }

    // Get display text and background color for action
    const actionConfig = this.getActionDisplay(action);
    this.lastActLabel.text = actionConfig.text;
    this.lastActLabel.style.fill = COLORS.lastAction.textFill;

    // Redraw colored background to fit text
    const padding = COLORS.lastAction.padding;
    const bgWidth = this.lastActLabel.width + padding.x * 2;
    const bgHeight = this.lastActLabel.height + padding.y * 2;

    this.lastActBg.clear();
    this.lastActBg.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, COLORS.lastAction.cornerRadius);
    this.lastActBg.fill(actionConfig.color);

    this.lastActContainer.visible = true;
  }

  /**
   * Get display text and color for an action
   */
  private getActionDisplay(action: string): { text: string; color: number } {
    switch (action) {
      case 'fold':
        return { text: 'FOLD', color: COLORS.lastAction.fold };
      case 'check':
        return { text: 'CHECK', color: COLORS.lastAction.check };
      case 'call':
        return { text: 'CALL', color: COLORS.lastAction.call };
      case 'bet':
        return { text: 'BET', color: COLORS.lastAction.bet };
      case 'raise':
        return { text: 'RAISE', color: COLORS.lastAction.raise };
      case 'allin':
        return { text: 'ALL IN', color: COLORS.lastAction.allin };
      default:
        return { text: action.toUpperCase(), color: COLORS.lastAction.default };
    }
  }

  /**
   * Create the winner badge (shows "WINNER" when player wins)
   */
  private createWinnerBadge(): void {
    // Clear existing children
    this.winnerBadge.removeChildren();

    const badgeText = new Text({
      text: 'WINNER',
      style: {
        fill: COLORS.winnerBadge.textFill,
        fontSize: this.bbs(SEAT_BREAKPOINTS.badgeFont) + 2,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    badgeText.anchor.set(0.5);

    const padding = COLORS.winnerBadge.padding;
    const badgeWidth = badgeText.width + padding * 2;
    const badgeHeight = badgeText.height + padding * 2;

    const badgeBg = new Graphics();
    badgeBg.roundRect(-badgeWidth / 2, -badgeHeight / 2, badgeWidth, badgeHeight, 4);
    badgeBg.fill(COLORS.winnerBadge.fill);

    this.winnerBadge.addChild(badgeBg);
    this.winnerBadge.addChild(badgeText);

    // Position badge below player info bar
    this.winnerBadge.x = 0;
    this.winnerBadge.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY) + 40;
  }

  // ===========================================================================
  // RESPONSIVE SIZING
  // ===========================================================================

  /**
   * Resize the seat view for screen width
   * Redraws all elements with new dimensions
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;

    this.currentScreenWidth = screenWidth;

    // Update text styles
    this.lastActLabel.style.fontSize = this.bbs(SEAT_BREAKPOINTS.badgeFont);

    // Update components for screen width
    this.playerInfoBar.resize(screenWidth);
    this.timerBorder.resize(screenWidth);
    this.sitoutBadge.resize(screenWidth);

    // Update positions
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.timerBorder.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.sitoutBadge.y = this.bbs(SEAT_BREAKPOINTS.lastActY);

    // Sync timer border geometry after PlayerInfoBar resize
    const geo = this.playerInfoBar.getGeometry();
    this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);
    this.lastActContainer.y = this.bbs(SEAT_BREAKPOINTS.lastActY);

    // Recreate winner badge with new sizes
    this.createWinnerBadge();

    // Redraw avatar if player is present
    if (this.player) {
      this.drawAvatar(this.player);
      // Also update avatar sprite size (drawAvatar handles same-id case)
      this.updateAvatarSpriteSize(this.bbs(SEAT_BREAKPOINTS.avatarSize));
    } else {
      this.showEmpty();
    }
  }

  // ===========================================================================
  // PUBLIC EFFECT INJECTION POINT ACCESSORS
  // ===========================================================================

  /** Get bet chips container (for ChipMoveEffect) - delegated to PlayerInfoBar */
  getBetChipsContainer(): Container {
    return this.playerInfoBar.getBetChipsContainer();
  }

  /** Get winner badge (for PlayerWinEffect) */
  getWinnerBadge(): Container {
    return this.winnerBadge;
  }

  /** Get timer border (for effect access) */
  getTimerBorder(): TimerBorder {
    return this.timerBorder;
  }

  /** Get avatar container (for avatar effects) */
  getAvatarContainer(): Container {
    return this.avatarContainer;
  }

  /** Get avatar glow (for player aura effects) */
  getAvatarGlow(): Graphics {
    return this.avatarGlow;
  }

  /** Get seat position for effect targeting */
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /** Get player chips display (for direct controller updates) */
  getPlayerChipsDisplay(): PlayerChipsDisplay {
    return this.playerInfoBar.getChipsDisplay();
  }

  /** Get player info bar (for direct access) */
  getPlayerInfoBar(): PlayerInfoBar {
    return this.playerInfoBar;
  }

  /** Get current player state (for profile popup) */
  getPlayer(): PlayerState | null {
    return this.player;
  }
}
